import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useContent } from '../../contexts/ContentContext';
import { getActiveNotifications, dismissNotification } from '../../config/notifications';
import NotificationPopup from './NotificationPopup';
import GlowIcon from '../GlowIcons';
import './SiteNotifications.css';

const VARIANT_ICON_MAP = {
  info: <GlowIcon name="radar" size={18} color="#00a4ef" />,
  success: <GlowIcon name="check" size={18} color="#20c997" />,
  warning: <GlowIcon name="lightning" size={18} color="#ffc107" />,
  promo: <GlowIcon name="star" size={18} color="#ff3366" />,
  error: <GlowIcon name="shield" size={18} color="#e63950" />
};

function NotificationCard({ item, layout, onDismiss, autoCloseMs }) {
  const [visible, setVisible] = useState(layout !== 'toast');
  const [leaving, setLeaving] = useState(false);

  const close = () => {
    setLeaving(true);
    window.setTimeout(() => {
      setVisible(false);
      if (item.dismissible) {
        dismissNotification(item.id, { sessionOnly: item.showOncePerSession });
      }
      onDismiss?.(item.id);
    }, 280);
  };

  useEffect(() => {
    if (layout !== 'toast' || !autoCloseMs) return undefined;
    const t = window.setTimeout(close, autoCloseMs);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, autoCloseMs]);

  if (!visible) return null;

  const isExternal = item.linkUrl && /^https?:\/\//i.test(item.linkUrl);
  const LinkTag = isExternal ? 'a' : Link;
  const linkProps = isExternal
    ? { href: item.linkUrl, target: '_blank', rel: 'noopener noreferrer' }
    : { to: item.linkUrl || '#' };

  return (
    <div
      className={`site-notify site-notify--${layout} site-notify--${item.variant} ${
        leaving ? 'site-notify--leave' : ''
      }`}
      role={layout === 'strip' ? 'banner' : 'status'}
      aria-live="polite"
    >
      <div className="site-notify__accent" aria-hidden="true" />
      <span className="site-notify__icon" aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center' }}>
        {VARIANT_ICON_MAP[item.variant] || <GlowIcon name="radar" size={18} color="#00a4ef" />}
      </span>
      <div className="site-notify__body">
        {item.title ? <strong className="site-notify__title">{item.title}</strong> : null}
        {item.message ? <p className="site-notify__message">{item.message}</p> : null}
        {item.linkText && item.linkUrl ? (
          <LinkTag className="site-notify__link" {...linkProps}>
            {item.linkText}
            <span aria-hidden="true"> →</span>
          </LinkTag>
        ) : null}
      </div>
      {item.dismissible ? (
        <button type="button" className="site-notify__close" onClick={close} aria-label="Kapat">
          ×
        </button>
      ) : null}
    </div>
  );
}

const SiteNotifications = () => {
  const { notifications } = useContent();
  const { pathname } = useLocation();
  const [dismissedSession, setDismissedSession] = useState([]);
  const isAdmin = pathname.startsWith('/admin');

  const active = useMemo(
    () => (isAdmin ? { popup: [], strip: [], banner: [], toast: [], settings: {} } : getActiveNotifications(notifications, pathname)),
    [notifications, pathname, isAdmin]
  );

  const onDismiss = (id) => setDismissedSession((prev) => [...prev, id]);

  const popupItems = active.popup.filter((i) => !dismissedSession.includes(i.id));
  const topItems = [
    ...active.strip.filter((i) => !dismissedSession.includes(i.id)),
    ...active.banner.filter((i) => !dismissedSession.includes(i.id))
  ];
  const toastItems = active.toast.filter((i) => !dismissedSession.includes(i.id));

  useEffect(() => {
    if (isAdmin || typeof document === 'undefined') return undefined;
    const h = topItems.length ? `${topItems.length * 52}px` : '0px';
    document.documentElement.style.setProperty('--site-notify-strip-h', h);
    document.body.classList.toggle('has-site-notify-strip', topItems.length > 0);
    return () => {
      document.body.classList.remove('has-site-notify-strip');
      document.documentElement.style.removeProperty('--site-notify-strip-h');
    };
  }, [topItems.length, isAdmin]);

  if (isAdmin) {
    return null;
  }

  const toastMs = active.settings?.toastDurationMs || 8000;

  return (
    <>
      {popupItems.map((item) => (
        <NotificationPopup
          key={item.id}
          item={item}
          settings={active.settings}
          onClose={onDismiss}
        />
      ))}

      {topItems.length > 0 ? (
        <div className="site-notify-stack site-notify-stack--strip">
          {topItems.map((item) => (
            <NotificationCard
              key={item.id}
              item={item}
              layout={item.type === 'banner' ? 'banner' : 'strip'}
              onDismiss={onDismiss}
            />
          ))}
        </div>
      ) : null}

      {toastItems.length > 0 ? (
        <div className="site-notify-stack site-notify-stack--toast" aria-label="Bildirimler">
          {toastItems.map((item) => (
            <NotificationCard
              key={item.id}
              item={item}
              layout="toast"
              onDismiss={onDismiss}
              autoCloseMs={toastMs}
            />
          ))}
        </div>
      ) : null}
    </>
  );
};

export default SiteNotifications;
